import {useCallback} from 'react';
import {useToken} from '@/components/TokenContext.tsx';
import type {OffBoardingSchemaType} from '@/pages/OffBoarding.tsx';
import type {OnBoardingSchemaType} from '@/pages/OnBoarding.tsx';

type Content = {
    type: string;
    text?: string;
    content?: Content[] | Content;
    attrs?: {
        localId?: string;
        state?: string;
    };
};

type Description = {
    type: string;
    version: number;
    content: Content[];
};

type Fields = {
    summary: string;
    issuetype: {
        id: string;
    };
    description?: Description;
    customfield_10124?: {
        id: string;
    };
    customfield_10015?: string;
    customfield_10158?: Array<{
        id: string;
    }>;
};

export type IssueCreateResponse = {
    id: string;
    key: string;
    self: string;
    transition?: {
        status: number;
        errorCollection: {
            errorMessages: unknown;
            errors: unknown;
        };
    };
};

export const useCreateIssue = (jiraInstance: string, project: number) => {
    const {token} = useToken();
    return useCallback(
        async (fields: Fields) => {
            const response = await fetch(
                `https://api.atlassian.com/ex/jira/${jiraInstance}/rest/api/3/issue`,
                {
                    method: 'POST',
                    headers: {
                        authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        fields: {
                            project: {
                                id: project,
                            },
                            ...fields,
                        },
                        update: {},
                    }),
                },
            );
            return (await response.json()) as IssueCreateResponse;
        },
        [token, jiraInstance, project],
    );
};

const createToDo = (text: string): Content => {
    return {
        type: 'taskItem',
        content: [
            {
                type: 'text',
                text,
            },
        ],
        attrs: {
            localId: self.crypto.randomUUID(),
            state: 'TODO',
        },
    };
};

const createToDoList = (items: string[]): Content => {
    return {
        type: 'taskList',
        content: items.map(text => createToDo(text)),
        attrs: {
            localId: self.crypto.randomUUID(),
        },
    };
};

const hrExternComsDescription = (): Description => {
    const items = [
        'Collected resignation letter',
        'Confirm who to forward email to',
        'Coordinate with manger for updating appropriate parties',
    ];
    return {
        type: 'doc',
        version: 1,
        content: [createToDoList(items)],
    };
};

const itAccountsDescription = (): Description => {
    const items = [
        'Changed AD password',
        'Changed lastpass password',
        'Disabled AD account',
        'Disabled zoom account',
        'Removed 365 licenses',
        'Removed zoom license',
    ];
    return {
        type: 'doc',
        version: 1,
        content: [createToDoList(items)],
    };
};

export const useCreateOffboarding = () => {
    const createIssue = useCreateIssue('6b2a920a-47a3-4926-a1a4-d61efe43ed72', 10000);
    return useCallback(
        async (user: OffBoardingSchemaType): Promise<IssueCreateResponse[]> => {
            const fullName = `${user.firstName} ${user.lastName}`;
            const tickets: IssueCreateResponse[] = [];
            tickets.push(
                await createIssue({
                    summary: `15Five ${fullName}`,
                    issuetype: {
                        id: '10042',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `ADP ${fullName}`,
                    issuetype: {
                        id: '10041',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Benefits ${fullName}`,
                    issuetype: {
                        id: '10045',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Benefits ${fullName}`,
                    issuetype: {
                        id: '10045',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `HR External Comms ${fullName}`,
                    issuetype: {
                        id: '10044',
                    },
                    description: hrExternComsDescription(),
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `HR Internal ${fullName}`,
                    issuetype: {
                        id: '10037',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `IT Account ${fullName}`,
                    issuetype: {
                        id: '10038',
                    },
                    description: itAccountsDescription(),
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `IT Assets ${fullName}`,
                    issuetype: {
                        id: '10039',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Tracker ${fullName}`,
                    issuetype: {
                        id: '10040',
                    },
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Office Access ${fullName}`,
                    issuetype: {
                        id: '10078',
                    },
                    description: hrExternComsDescription(),
                }),
            );
            return tickets;
        },
        [createIssue],
    );
};

export const useCreateOnboarding = () => {
    const createIssue = useCreateIssue('75b01570-cb3d-4525-8f3c-3ee4203e0b4a', 11111);

    return useCallback(
        async (user: OnBoardingSchemaType): Promise<IssueCreateResponse[]> => {
            const tickets: IssueCreateResponse[] = [];
            const baseFields: Partial<Fields> = {
                customfield_10124: {
                    id: user.associateType,
                },
                customfield_10015: user.startDate.toString(),
            };
            const fullName = `${user.firstName} ${user.lastName}`;
            tickets.push(
                await createIssue({
                    summary: `ID Verification ${fullName}`,
                    issuetype: {
                        id: '10109',
                    },
                    ...baseFields,
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Background Check ${fullName}`,
                    issuetype: {
                        id: '10108',
                    },
                    ...baseFields,
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Reference Check ${fullName}`,
                    issuetype: {
                        id: '10182',
                    },
                    ...baseFields,
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Create Tracker Record ${fullName}`,
                    issuetype: {
                        id: '10183',
                    },
                    ...baseFields,
                }),
            );
            if (user.associateType === '10119') {
                tickets.push(
                    await createIssue({
                        summary: `Procure IT Equipment ${fullName}`,
                        issuetype: {
                            id: '10110',
                        },
                        ...(user.computerPreference
                            ? {
                                  customfield_10125: {
                                      id: user.computerPreference,
                                  },
                              }
                            : {}),
                        ...baseFields,
                    }),
                );
            }
            tickets.push(
                await createIssue({
                    summary: `Create IT Accounts ${fullName}`,
                    issuetype: {
                        id: '10111',
                    },
                    customfield_10158: user.groups.map(g => ({
                        id: g,
                    })),
                    ...baseFields,
                }),
            );
            tickets.push(
                await createIssue({
                    summary: `Setup Office Access ${fullName}`,
                    issuetype: {
                        id: '10325',
                    },
                    ...baseFields,
                }),
            );

            return tickets;
        },
        [createIssue],
    );
};
